<template>
  <b-modal
    id="modal-batch-list"
    ref="modalBatchList"
    title="Select Batch"
    size="xl"
    hide-footer
    @shown="fetchBatches"
    @hidden="resetModal">
    <div>
      <!-- Course Info Header -->
      <div class="mb-4">
        <div class="row">
          <div class="col-6">
            <h5 class="mb-2">{{ selectedCourse?.title }}</h5>
            <p class="text-muted mb-0">{{ selectedCourse?.description }}</p>
          </div>
          <div class="col-6 d-flex justify-content-end align-items-start gap-2">
            <button
              type="button"
              class="btn btn-sm btn-outline-info"
              @click="downloadTemplate"
              :disabled="isDownloading || batches.length === 0">
              <span v-if="isDownloading">
                <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Mengunduh...
              </span>
              <span v-else>
                <i class="fe fe-download me-1"></i>
                Download Template
              </span>
            </button>
            <button 
              type="button"
              class="btn btn-sm btn-success"
              @click="showBulkUploadModal"
              :disabled="batches.length === 0">
              <i class="fe fe-upload me-1"></i>
              Upload Excel
            </button>
          </div>
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-md-4">
          <div class="form-group">
            <label class="form-label">Status</label>
            <v-select
              v-model="selectedStatus"
              :options="participantStatusOptions"
              :reduce="option => option.value"
              label="label"
              placeholder="Semua status"
              :clearable="true">
            </v-select>
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-group">
            <label class="form-label">Approval</label>
            <v-select
              v-model="selectedApprovalStep"
              :options="approvalStepOptions"
              :reduce="option => option.value"
              label="label"
              placeholder="Semua approval"
              :clearable="true">
            </v-select>
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-group">
            <label class="form-label">Course Type</label>
            <v-select
              v-model="selectedCourseType"
              :options="courseTypeOptions"
              :reduce="option => option.value"
              label="label"
              placeholder="Semua tipe"
              :clearable="true">
            </v-select>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="batchLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-2 text-muted">Loading batches...</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="batches.length === 0" class="text-center py-5">
        <i class="fe fe-inbox" style="font-size: 3rem; color: #dee2e6;"></i>
        <p class="mt-3 text-muted mb-0">No batches available</p>
      </div>

      <!-- Batch Table -->
      <div v-else>
        <vue-good-table
          :columns="batchColumns"
          :rows="batches"
          :pagination-options="{ enabled: true }"
        >
          <template slot="table-row" slot-scope="props">
            <span v-if="props.column.field === 'batch'">
              {{ formatBatchLabel(props.row) }}
            </span>
            <span v-else-if="props.column.field === 'batch_status'">
              <span class="badge" :class="getBatchStatusClass(props.row.batch_status)">
                {{ props.row.batch_status }}
              </span>
            </span>
            <span v-else-if="props.column.field === 'schedule'">
              <template v-for="schedules in [getBatchSchedules(props.row)]">
                <div v-if="schedules.length">
                  <div
                    v-for="(schedule, idx) in schedules"
                    :key="idx"
                    :class="{ 'mt-2': idx > 0 }"
                  >
                    <strong class="d-block">{{ schedule.date }}</strong>
                    <small v-if="schedule.time" class="text-muted">{{ schedule.time }}</small>
                  </div>
                </div>
                <span v-else class="text-muted">No schedule</span>
              </template>
            </span>
            <span v-else-if="props.column.field === 'penyelenggara'">
              {{ formatPenyelenggara(props.row.penyelenggara) }}
            </span>
            <span v-else-if="props.column.field === 'enrolled'">
              {{ getEnrolledCount(props.row) }}
            </span>
            <span v-else-if="props.column.field === 'remaining_quota'">
              {{ getRemainingQuotaWithDrafts(props.row) ?? '-' }}
            </span>
            <span v-else-if="props.column.field === 'quota_status'">
              <span v-if="props.row.quota_status" class="badge badge-soft-danger">
                {{ props.row.quota_status }}
              </span>
              <span v-else>-</span>
            </span>
            <span v-else-if="props.column.field === 'action'">
              <button class="btn btn-sm btn-outline-primary" @click="selectBatch(props.row, props.index)">
                <i class="fe fe-users me-1"></i> View
              </button>
            </span>
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>
      </div>
    </div>

    <!-- Bulk Upload Modal -->
    <modal-bulk-upload
      :selectedCourseId="selectedCourse?.id"
      :idpPeriod="idpPeriod"
      @upload-success="handleUploadSuccess"
    />
  </b-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import ModalBulkUpload from './modal-bulk-upload.vue';
import { normalizeErrorMessage } from '@/utils/error-message';
import { formatBatchLabel, getBatchSchedules } from '@/utils/batchIdentity';
import { getEnrolledCount, getRemainingQuotaWithDrafts } from '@/utils/enrollmentQuota';
import {
  APPROVAL_STEP_SLUG_OPTIONS,
  COURSE_TYPE_BOOLEAN_OPTIONS,
  PARTICIPANT_STATUS_OPTIONS,
  buildMitraFilterParams
} from '@/constants/mitraFilters';

export default {
  name: 'ModalBatchList',
  props: {
    selectedCourse: {
      type: Object,
      default: null
    },
    idpPeriod: {
      type: [Number, String],
      default: null
    },
    targetTrainingEventId: {
      type: [String, Number],
      default: null
    }
  },
  components: {
    VueGoodTable,
    ModalBulkUpload
  },
  computed: {
    ...mapGetters('mitraEnrollment', ['batches', 'batchLoading'])
  },
  data() {
    return {
      isDownloading: false,
      selectedStatus: null,
      selectedApprovalStep: null,
      selectedCourseType: null,
      participantStatusOptions: PARTICIPANT_STATUS_OPTIONS,
      approvalStepOptions: APPROVAL_STEP_SLUG_OPTIONS,
      courseTypeOptions: COURSE_TYPE_BOOLEAN_OPTIONS,
      hasOpenedTargetBatch: false,
      batchColumns: [
        { label: 'Batch', field: 'batch' },
        { label: 'Status', field: 'batch_status' },
        { label: 'Schedule', field: 'schedule', width: '240px' },
        { label: 'Penyelenggara', field: 'penyelenggara', width: '180px' },
        { label: 'Quota', field: 'quota' },
        { label: 'Enrolled', field: 'enrolled' },
        { label: 'Sisa', field: 'remaining_quota' },
        { label: 'Quota Status', field: 'quota_status', width: '130px' },
        { label: 'Participants', field: 'action', width: '120px' }
      ]
    };
  },
  watch: {
    selectedStatus() {
      this.refetchBatchesIfVisible();
    },
    selectedApprovalStep() {
      this.refetchBatchesIfVisible();
    },
    selectedCourseType() {
      this.refetchBatchesIfVisible();
    }
  },
  methods: {
    participantFilterParams() {
      return buildMitraFilterParams({
        status: this.selectedStatus,
        approval_step: this.selectedApprovalStep,
        course_type: this.selectedCourseType
      }, { includeParticipantStatusFromApproval: true });
    },
    refetchBatchesIfVisible() {
      if (!this.$refs.modalBatchList || !this.$refs.modalBatchList.isVisible) return;
      this.fetchBatches();
    },
    async fetchBatches() {
      if (!this.selectedCourse?.id) return;
      
      try {
        const params = {
          ...this.participantFilterParams()
        };
        if (this.idpPeriod) {
          params.idp_period = this.idpPeriod;
        }
        
        await this.$store.dispatch('mitraEnrollment/getBatches', {
          courseId: this.selectedCourse.id,
          params
        });
        this.openTargetBatch();
      } catch (error) {
        console.error('Error fetching batches:', error);
        this.$swal({
          title: 'Error',
          text: normalizeErrorMessage(error.message || 'Daftar batch belum berhasil dimuat'),
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      }
    },
    
    resetModal() {
      this.selectedStatus = null;
      this.selectedApprovalStep = null;
      this.selectedCourseType = null;
      this.hasOpenedTargetBatch = false;
    },
    
    getEnrolledCount,
    getRemainingQuotaWithDrafts,

    selectBatch(batch, index) {
      this.$emit('batch-selected', { batch, index });
    },

    openTargetBatch() {
      if (!this.targetTrainingEventId || this.hasOpenedTargetBatch) return;

      const index = (this.batches || []).findIndex(
        batch => String(batch.training_event_id) === String(this.targetTrainingEventId)
      );
      if (index < 0) return;

      this.hasOpenedTargetBatch = true;
      this.selectBatch(this.batches[index], index);
    },
    
    formatBatchLabel,
    getBatchSchedules,
    formatPenyelenggara(value) {
      const organizerLabels = {
        'PT BORNEO INDOBARA': 'Borneo Indobara',
        'PT CIPTA KRIDATAMA': 'Cipta Kridatama',
        'PT TAMA MULIA RESOURCES': 'Tama Mulia Resources',
        'PT PUTRA PERKASA ABADI': 'Putra Perkasa Abadi'
      };

      return organizerLabels[value] || value || '-';
    },
    getBatchStatusClass(status) {
      const classes = {
        'upcoming': 'badge-soft-warning',
        'ongoing': 'badge-soft-success',
        'completed': 'badge-soft-info',
        'cancelled': 'badge-soft-danger'
      };
      return classes[status?.toLowerCase()] || 'badge-soft-secondary';
    },

    async downloadTemplate() {
      if (this.isDownloading) return;
      
      if (!this.selectedCourse?.id) {
        this.$swal({
          title: 'Warning',
          text: 'Pilih pelatihan terlebih dahulu.',
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
        return;
      }
      
      this.isDownloading = true;
      try {
        const params = {};
        if (this.idpPeriod) {
          params.idp_period = this.idpPeriod;
        }
        
        const response = await this.$store.dispatch('mitraEnrollment/downloadEnrollmentTemplate', {
          courseId: this.selectedCourse.id,
          params
        });
        
        // Create blob and download
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Enrollment_Template_${this.selectedCourse.title || 'Course'}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        this.$swal({
          title: 'Success',
          text: 'Template berhasil didownload.',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#2c7be5'
        });
      } catch (error) {
        console.error('Error downloading template:', error);
        this.$swal({
          title: 'Error',
          text: 'Template belum berhasil didownload.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      } finally {
        this.isDownloading = false;
      }
    },

    showBulkUploadModal() {
      if (!this.selectedCourse?.id) {
        this.$swal({
          title: 'Warning',
          text: 'Tidak ada pelatihan yang dipilih',
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
        return;
      }
      this.$bvModal.show('modal-bulk-upload-enrollment');
    },

    handleUploadSuccess(result) {
      console.log('✅ Upload success:', result);
      // Refresh batches after successful upload
      this.fetchBatches();
    }
  }
};
</script>

<style scoped>
.batch-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 2px solid #e9ecef;
}

.batch-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  border-color: #e63757;
}

.badge-soft-warning {
  color: #f6c343;
  background-color: rgba(246, 195, 67, 0.1);
}

.badge-soft-success {
  color: #00c9a7;
  background-color: rgba(0, 201, 167, 0.1);
}

.badge-soft-info {
  color: #39afd1;
  background-color: rgba(57, 175, 209, 0.1);
}

.badge-soft-danger {
  color: #e63757;
  background-color: rgba(230, 55, 87, 0.1);
}

.badge-soft-secondary {
  color: #6c757d;
  background-color: rgba(108, 117, 125, 0.1);
}

.me-1 {
  margin-right: 0.25rem;
}
</style>
